import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { myId } from "../lib/me";
import { shortDate } from "../lib/weekLine";
import { href } from "../lib/route";
import { defaultProgramName, programDaysOf, saveWeekAsProgram, type ProgramRpc } from "../lib/weekProgram";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * WEEK EDITOR STAGE 2 — SAVE THIS WEEK AS A PROGRAM (2026-10-19, `docs/WEEK-EDITOR-STAGE2-PLAN.md` §4). A dimmed window: the
 * name, the days it will hold, one sentence on what happens, Save. ONE call (`save_week_as_program`): every workout copied into
 * the library, the program, its days — or nothing. Workouts only (a day's tasks are not part of a program).
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export type WeekToSave = { mondayKey: string; days: { dayKey: string; workouts: { workoutId: string; name: string }[] }[] };

export function SaveWeekAsProgram({ week, clientName, onClose }: {
  week: WeekToSave;
  clientName?: string;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const monday = shortDate(week.mondayKey).replace(/^\S+\s/, "");
  const [name, setName] = useState(() => defaultProgramName(clientName, monday));
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    const el = dialog.current;
    if (el !== null && !el.open) el.showModal();
    field.current?.select();
  }, []);
  const days = week.days.filter((d) => d.workouts.length > 0);
  const count = days.reduce((n, d) => n + d.workouts.length, 0);

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaid(null);
    const lib = await supabase.from("coach_workouts").select("name").eq("coach_id", await myId()).is("client_id", null);
    const names = (lib.data ?? []).map((r) => r.name);
    const r = await saveWeekAsProgram(supabase.rpc.bind(supabase) as unknown as ProgramRpc, name, programDaysOf(days, names));
    setBusy(false);
    if (!r.ok) { setSaid(r.said); return; }
    setSaved({ id: r.programId, name: name.trim() });
  }

  if (saved !== null) {
    return (
      <dialog ref={dialog} className="ht-window ht-confirm week-program" aria-labelledby="week-program-title"
              onCancel={(e) => { e.preventDefault(); onClose(); }}>
        <div className="ht-window-frame ht-confirm-frame week-program-frame">
          <h2 className="ht-title ht-confirm-title" id="week-program-title">Saved</h2>
          <p className="ht-confirm-sentence" role="status">{saved.name} is in your programs.</p>
          <div className="ht-confirm-actions">
            <a className="ht-button" href={href({ name: "program", programId: saved.id })}>Open program</a>
            <button type="button" className="ht-button ht-button-primary" autoFocus onClick={onClose}>Done</button>
          </div>
        </div>
      </dialog>
    );
  }

  return (
    <dialog ref={dialog} className="ht-window ht-confirm week-program" aria-labelledby="week-program-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <form className="ht-window-frame ht-confirm-frame week-program-frame" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <h2 className="ht-title ht-confirm-title" id="week-program-title">Save as program</h2>
        <label className="week-program-field">
          <span className="ht-overline">Name</span>
          <input ref={field} className="ht-input" value={name} maxLength={120} required
                 onChange={(e) => { setName(e.target.value); }} />
        </label>
        <p className="week-program-days">
          {count === 0 ? "This week has no workouts to save." : days.map((d) => d.workouts.map((w) => `${shortDate(d.dayKey).split(" ")[0]} ${w.name}`).join(" · ")).join(" · ")}
        </p>
        <p className="ht-confirm-sentence">Each workout is copied into your library; the program is one week long.</p>
        {said !== null && <p className="ht-callout" role="alert">{said}</p>}
        <div className="ht-confirm-actions">
          <button type="button" className="ht-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="ht-button ht-button-primary" disabled={busy || count === 0 || name.trim() === ""}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
